import { forwardRef, useCallback, useMemo } from 'react';
import type { ComponentProps } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
  type To,
} from 'react-router-dom';
import { AppProvider } from '@toolpad/core/AppProvider';
import type { AppProviderProps } from '@toolpad/core/AppProvider';
import { getTestId, toKebabCase } from '@locus-taxy/encore-utils';
import {
  appendTeamsToHref,
  appendTeamsToTo,
  extractTeamsParam,
} from '../utils/teamsQueryParam';
import { TestIdAction } from '../utils/testIdUtils';

type NavigateTarget = To | number;
type HistoryBehavior = 'auto' | 'push' | 'replace';
type NavigateOptions = { history?: HistoryBehavior };
type ToolpadLinkProps = Omit<ComponentProps<typeof RouterLink>, 'to'> & {
  href: string;
  history?: HistoryBehavior;
};

interface EncoreRouterAppProviderProps extends AppProviderProps {
  /**
   * Optional override for the pathname used by Toolpad to determine active navigation items.
   * Useful when the URL structure (flat) differs from the navigation structure (nested/grouped).
   */
  toolpadPath?: string;
}

/**
 * Ensures a `teams` query parameter is retained when Toolpad renders sidebar links
 * or when internal navigation triggers `navigate(...)`. This keeps the currently
 * selected team scope active as users explore new entities.
 */
export function EncoreRouterAppProvider({
  toolpadPath,
  ...props
}: Readonly<EncoreRouterAppProviderProps>) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const teamsParam = extractTeamsParam(searchParams);

  const ensureTeamsInHref = useCallback(
    (href: string): string => {
      return appendTeamsToHref(href, teamsParam);
    },
    [teamsParam]
  );

  const ensureTeamsInTo = useCallback(
    (to: To): To => {
      return appendTeamsToTo(to, teamsParam);
    },
    [teamsParam]
  );

  const applyTeamsParam = useCallback(
    (target: NavigateTarget): NavigateTarget => {
      if (typeof target === 'number' || !teamsParam) {
        return target;
      }
      return ensureTeamsInTo(target);
    },
    [ensureTeamsInTo, teamsParam]
  );

  const Link = useMemo(
    () =>
      forwardRef<HTMLAnchorElement, ToolpadLinkProps>(
        ({ href, history, ...rest }, ref) => {
          // Extract the last non-empty segment from the URL path as a generic slug
          const segments = href.split('/').filter(Boolean);
          const lastSegment =
            segments.length > 0 ? segments[segments.length - 1] : 'home';
          const slug = toKebabCase(lastSegment);

          const testId = getTestId({
            action: TestIdAction.NAVIGATE,
            object: 'nav-item',
            entity: slug,
            page: 'sidebar',
          });

          return (
            <RouterLink
              ref={ref}
              to={ensureTeamsInHref(href)}
              replace={history === 'replace'}
              data-testid={testId}
              {...rest}
            />
          );
        }
      ),
    [ensureTeamsInHref]
  );

  const navigateImpl = useCallback(
    (to: NavigateTarget, { history = 'auto' }: NavigateOptions = {}) => {
      const destination = applyTeamsParam(to);
      if (typeof destination === 'number') {
        navigate(destination);
        return;
      }
      if (history === 'replace') {
        navigate(destination, { replace: true });
        return;
      }
      navigate(destination);
    },
    [applyTeamsParam, navigate]
  );

  const routerImpl = useMemo(
    () => ({
      // Use the toolpadPath if provided (for active item highlighting), otherwise fallback to real pathname
      pathname: toolpadPath ?? pathname,
      searchParams,
      navigate: navigateImpl,
      Link,
    }),
    [pathname, toolpadPath, searchParams, navigateImpl, Link]
  );

  return <AppProvider router={routerImpl} {...props} />;
}
